import React, { useState } from 'react';
import { trackFormSubmission } from '../Analytics/Analytics';
import './ContactForm.css';

const HUBSPOT_PORTAL_ID = '25730550';
const HUBSPOT_FORM_GUID = '07338dd3-a5af-40fc-8817-ad9235fbab0c';
const CONTACT_EMAIL = 'comercial@e-asy.es';
const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;
const HABLEMOS_WEBHOOK_URL = process.env.REACT_APP_HABLEMOS_WEBHOOK_URL;

const initialFormData = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  company: '',
  message: '',
};

const ContactForm = ({
  darkMode,
  source = 'contacto',
  trackingEvent = 'contact_form',
  pageName = 'Contacto',
  subjectPrefix = '[Contacto]',
  emptyMessageFallback = 'Solicitud de consultoría desde /contacto',
}) => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmed = {
      firstname: formData.firstname.trim(),
      lastname: formData.lastname.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      company: formData.company.trim(),
      message: formData.message.trim(),
    };

    if (!trimmed.firstname || !trimmed.email) {
      setError('Nombre y email son obligatorios.');
      return;
    }

    const messageBody = [
      `Origen: ${source}`,
      trimmed.company ? `Empresa: ${trimmed.company}` : null,
      trimmed.message || emptyMessageFallback,
    ]
      .filter(Boolean)
      .join('\n\n');

    setLoading(true);
    try {
      const subject = `${subjectPrefix} Consulta de ${trimmed.firstname} ${trimmed.lastname}`.trim();

      if (FORMSPREE_ID) {
        const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nombre: trimmed.firstname,
            apellidos: trimmed.lastname,
            email: trimmed.email,
            teléfono: trimmed.phone,
            empresa: trimmed.company,
            origen: source,
            mensaje: messageBody,
            _subject: subject,
          }),
        });
        if (!res.ok) throw new Error('Error al enviar');
      } else if (HABLEMOS_WEBHOOK_URL) {
        const emailBody = [
          `Nombre: ${trimmed.firstname} ${trimmed.lastname}`.trim(),
          `Email: ${trimmed.email}`,
          trimmed.phone ? `Teléfono: ${trimmed.phone}` : null,
          trimmed.company ? `Empresa: ${trimmed.company}` : null,
          `Origen: ${source}`,
          '',
          messageBody,
        ]
          .filter(Boolean)
          .join('\n');
        const res = await fetch(HABLEMOS_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: CONTACT_EMAIL,
            cc: '',
            cc1: '',
            cc2: '',
            cc3: '',
            subject,
            text: emailBody,
          }),
        });
        if (!res.ok) throw new Error('Error al enviar');
      } else {
        const fields = [
          { name: 'firstname', value: trimmed.firstname },
          { name: 'lastname', value: trimmed.lastname },
          { name: 'email', value: trimmed.email },
          { name: 'phone', value: trimmed.phone },
          { name: 'message', value: messageBody },
        ];
        if (trimmed.company) {
          fields.push({ name: 'company', value: trimmed.company });
        }

        const res = await fetch(
          `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fields,
              context: { pageUri: window.location.href, pageName },
            }),
          }
        );
        if (!res.ok) throw new Error('Error al enviar');
      }

      trackFormSubmission(trackingEvent);
      setSent(true);
      setFormData(initialFormData);
    } catch (err) {
      setError(`No se pudo enviar. Escribe a ${CONTACT_EMAIL}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`contact-form-root ${darkMode ? 'dark' : 'light'}`}>
      {sent ? (
        <div className="contact-form-success" role="status">
          <h3 className="contact-success-title">Gracias</h3>
          <p className="contact-success-text">
            Hemos recibido tu mensaje. Te contestaremos lo antes posible.
          </p>
          <button
            type="button"
            className="contact-submit"
            onClick={() => setSent(false)}
          >
            Enviar otro mensaje
          </button>
        </div>
      ) : (
        <form className="contact-native-form" onSubmit={handleSubmit} noValidate>
          <div className="contact-input-row">
            <label className="contact-input-label">
              <span className="contact-label-text">
                Nombre <span className="contact-required">*</span>
              </span>
              <input
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                required
                autoComplete="given-name"
                className="contact-input"
                placeholder="Tu nombre"
              />
            </label>
            <label className="contact-input-label">
              <span className="contact-label-text">Apellidos</span>
              <input
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                autoComplete="family-name"
                className="contact-input"
                placeholder="Tus apellidos"
              />
            </label>
          </div>

          <label className="contact-input-label">
            <span className="contact-label-text">
              Email <span className="contact-required">*</span>
            </span>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              inputMode="email"
              className="contact-input"
              placeholder="tu@email.com"
            />
          </label>

          <label className="contact-input-label">
            <span className="contact-label-text">Teléfono</span>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
              inputMode="tel"
              className="contact-input"
              placeholder="Opcional"
            />
          </label>

          <label className="contact-input-label">
            <span className="contact-label-text">Empresa</span>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              autoComplete="organization"
              className="contact-input"
              placeholder="Nombre de tu empresa u organización"
            />
          </label>

          <label className="contact-input-label">
            <span className="contact-label-text">Mensaje</span>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="contact-textarea"
              placeholder="Cuéntanos brevemente tu proyecto o duda..."
              rows={4}
            />
          </label>

          {error && (
            <p className="contact-form-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="contact-submit" disabled={loading}>
            {loading ? 'Enviando…' : 'Enviar'}
          </button>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
